import { PublicShowGroupLink } from "../../models/show_group_link";
import { createSupabaseAdminClient } from "../../supabase/supabase";

const PUBLIC_COLUMNS =
  "id, venue_page_id, title, slug, link_type, event_ids, start_date, end_date, day_of_week, show_time, show_name, facebook_pixel_id, created_at, updated_at";

/**
 * List all show_group_link rows for a venue page, freshest first.
 *
 * Uses the service-role client so the table doesn't need a public SELECT
 * policy, avoiding exposure of the created_by column.
 */
export async function listShowGroupLinksForVenuePublic(params: {
  venuePageId: string;
}): Promise<PublicShowGroupLink[]> {
  const supabase = createSupabaseAdminClient();
  const { data, error } = await (supabase as any) // eslint-disable-line @typescript-eslint/no-explicit-any
    .from("show_group_link")
    .select(PUBLIC_COLUMNS)
    .eq("venue_page_id", params.venuePageId)
    .order("updated_at", { ascending: false });

  if (error) throw error;
  return (data as PublicShowGroupLink[]) ?? [];
}

/**
 * Fetch a single show_group_link row by (venue_page_id, slug).
 * Returns null when no row matches.
 *
 * Uses the service-role client so the table doesn't need a public SELECT
 * policy, avoiding exposure of the created_by column.
 */
export async function getShowGroupLinkBySlugPublic(params: {
  venuePageId: string;
  slug: string;
}): Promise<PublicShowGroupLink | null> {
  const supabase = createSupabaseAdminClient();
  const { data, error } = await (supabase as any) // eslint-disable-line @typescript-eslint/no-explicit-any
    .from("show_group_link")
    .select(PUBLIC_COLUMNS)
    .eq("venue_page_id", params.venuePageId)
    .eq("slug", params.slug)
    .maybeSingle();

  if (error) throw error;
  return (data as PublicShowGroupLink) ?? null;
}

/**
 * Find a `showname`-type show_group_link row at the given venue whose
 * `show_name` matches the given name (case-insensitive, trimmed). Used by
 * the consumer page to inherit an admin's configured Facebook Pixel / title
 * onto the auto-resolved "All Events" view so grid clicks pick up the same
 * customization the admin applied to their explicit share link.
 *
 * Returns null when nothing matches.
 *
 * Uses the service-role client so the table doesn't need a public SELECT
 * policy, avoiding exposure of the created_by column.
 */
export async function findShownameShowGroupByShowName(params: {
  venuePageId: string;
  showName: string;
}): Promise<PublicShowGroupLink | null> {
  const supabase = createSupabaseAdminClient();
  const { data, error } = await (supabase as any) // eslint-disable-line @typescript-eslint/no-explicit-any
    .from("show_group_link")
    .select(PUBLIC_COLUMNS)
    .eq("venue_page_id", params.venuePageId)
    .eq("link_type", "showname")
    .ilike("show_name", params.showName.trim())
    // Earliest-created row wins when multiple admin rows configure the same
    // show_name. Deterministic so inheritance doesn't flip across requests.
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return (data as PublicShowGroupLink) ?? null;
}
